import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usersAPI, perksAPI } from '../services/api';

const PERSONAS = [['', 'All personas'], ['founder', 'Founder'], ['traveler', 'Traveler'], ['both', 'Both']];
const PAGE_SIZE = 25;

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};
const relDays = (d) => {
  if (!d) return null;
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const emptyFilters = { search: '', persona: '', active: '', verified: '', neverLoggedIn: '', from: '', to: '', sort: 'last_login_at', order: 'desc' };

export default function Users() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await usersAPI.getUsers({ ...filters, page, limit: PAGE_SIZE });
      const d = res.data.data;
      setRows(d.users || []);
      setPagination(d.pagination || { total: 0, page: 1, totalPages: 1 });
      setStats(d.stats || {});
    } catch (e) {
      setError('Failed to load users');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const onSearch = (v) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setFilter('search', v), 350);
  };
  const toggleSort = (col) => {
    setPage(1);
    setFilters((f) => ({ ...f, sort: col, order: f.sort === col && f.order === 'desc' ? 'asc' : 'desc' }));
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await usersAPI.exportUsers(filters);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `naytive-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const th = (label, col) => (
    <th onClick={col ? () => toggleSort(col) : undefined}
      style={{ ...S.th, cursor: col ? 'pointer' : 'default', userSelect: 'none' }}>
      {label}{col && filters.sort === col ? (filters.order === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );

  const statCard = (label, value, accent) => (
    <div style={S.statCard}>
      <div style={{ ...S.statValue, color: accent || '#111827' }}>{value ?? 0}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Users</h1>
          <p style={S.subtitle}>Everyone on the Naytive platform · last login doubles as last visit.</p>
        </div>
        <button onClick={doExport} disabled={exporting} style={{ ...S.exportBtn, opacity: exporting ? 0.6 : 1 }}>
          {exporting ? 'Exporting…' : '⭳ Export CSV'}
        </button>
      </div>

      <div style={S.statsRow}>
        {statCard('Total users', stats.total, '#2540D9')}
        {statCard('Active (7 days)', stats.active7d, '#059669')}
        {statCard('Active (30 days)', stats.active30d, '#059669')}
        {statCard('Never logged in', stats.neverLoggedIn, '#B45309')}
      </div>

      <div style={S.filters}>
        <input placeholder="Search email, name or phone…" defaultValue={filters.search}
          onChange={(e) => onSearch(e.target.value)} style={{ ...S.input, minWidth: 240, flex: 1 }} />
        <select value={filters.persona} onChange={(e) => setFilter('persona', e.target.value)} style={S.input}>
          {PERSONAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.active} onChange={(e) => setFilter('active', e.target.value)} style={S.input}>
          <option value="">Any status</option><option value="true">Active</option><option value="false">Inactive</option>
        </select>
        <select value={filters.verified} onChange={(e) => setFilter('verified', e.target.value)} style={S.input}>
          <option value="">Any verification</option><option value="true">Email verified</option><option value="false">Unverified</option>
        </select>
        <label style={S.checkLabel}>
          <input type="checkbox" checked={filters.neverLoggedIn === 'true'}
            onChange={(e) => setFilter('neverLoggedIn', e.target.checked ? 'true' : '')} /> Never logged in
        </label>
        <div style={S.dateGroup}>
          <span style={S.dateLbl}>Last login</span>
          <input type="date" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} style={S.input} />
          <span style={{ color: '#94A3B8' }}>→</span>
          <input type="date" value={filters.to} onChange={(e) => setFilter('to', e.target.value)} style={S.input} />
        </div>
        {(filters.search || filters.persona || filters.active || filters.verified || filters.neverLoggedIn || filters.from || filters.to) && (
          <button onClick={() => { setPage(1); setFilters(emptyFilters); }} style={S.clearBtn}>Clear</button>
        )}
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {th('Name')}
              {th('Email', 'email')}
              {th('Phone / WhatsApp')}
              {th('Persona')}
              {th('Signup')}
              {th('Verified')}
              {th('Joined', 'created_at')}
              {th('Last login / visit', 'last_login_at')}
              {th('Pro')}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={S.msgCell}>Loading…</td></tr>
            ) : error ? (
              <tr><td colSpan={9} style={{ ...S.msgCell, color: '#DC2626' }}>{error}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} style={S.msgCell}>No users match these filters.</td></tr>
            ) : rows.map((u) => {
              const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
              return (
                <tr key={u.id} style={S.tr}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 500, color: name ? '#111827' : '#94A3B8' }}>{name || '—'}</div>
                  </td>
                  <td style={S.td}>{u.email || '—'}</td>
                  <td style={S.td}>
                    {u.phone ? <div>{u.phone}</div> : null}
                    {u.whatsapp_number ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: u.phone ? 3 : 0 }}>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#fff', background: '#25D366', borderRadius: 4, padding: '1px 5px' }}>WA</span>
                        {u.whatsapp_number}
                      </div>
                    ) : null}
                    {!u.phone && !u.whatsapp_number ? '—' : null}
                  </td>
                  <td style={S.td}>{u.persona ? <span style={S.pill}>{u.persona}</span> : '—'}</td>
                  <td style={S.td}>{u.signup_method || '—'}</td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: u.email_verified ? '#DCFCE7' : '#FEF3C7', color: u.email_verified ? '#166534' : '#92400E' }}>
                      {u.email_verified ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDate(u.created_at)}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    {u.last_login_at ? (
                      <>
                        <span>{fmtDate(u.last_login_at)}</span>
                        <span style={{ fontSize: 12, color: '#94A3B8', marginLeft: 8 }}>{relDays(u.last_login_at)}</span>
                      </>
                    ) : <span style={{ color: '#B45309' }}>Never</span>}
                  </td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={async () => {
                        const next = u.pro_override ? 0 : 1;
                        setRows((rs) => rs.map((x) => (x.id === u.id ? { ...x, pro_override: next } : x)));
                        try { await perksAPI.setUserPro(u.id, next === 1); }
                        catch { setRows((rs) => rs.map((x) => (x.id === u.id ? { ...x, pro_override: u.pro_override } : x))); }
                      }}
                      title={u.pro_override ? 'Pro access granted — click to revoke' : 'Grant Pro (perks) access'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                        border: '1px solid ' + (u.pro_override ? '#A7F3D0' : '#E5E7EB'),
                        background: u.pro_override ? '#ECFDF5' : '#fff',
                        color: u.pro_override ? '#047857' : '#6B7280',
                        borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 600,
                      }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: u.pro_override ? '#10B981' : '#CBD5E1' }} />
                      {u.pro_override ? 'Pro' : 'Grant'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={S.footer}>
        <span style={{ color: '#64748B', fontSize: 13 }}>
          {pagination.total.toLocaleString()} user{pagination.total === 1 ? '' : 's'}
          {pagination.total > 0 && ` · page ${pagination.page} of ${pagination.totalPages}`}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ ...S.pageBtn, opacity: page <= 1 ? 0.5 : 1 }}>← Prev</button>
          <button disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)} style={{ ...S.pageBtn, opacity: page >= pagination.totalPages ? 0.5 : 1 }}>Next →</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '28px 32px', fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif', color: '#111827' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
  title: { fontSize: 24, fontWeight: 600, margin: 0 },
  subtitle: { fontSize: 13.5, color: '#64748B', margin: '4px 0 0' },
  exportBtn: { background: '#2540D9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 },
  statCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px' },
  statValue: { fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  statLabel: { fontSize: 12.5, color: '#64748B', marginTop: 2 },
  filters: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 12 },
  input: { border: '1px solid #D1D5DB', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', color: '#111827', background: '#fff' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' },
  dateGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  dateLbl: { fontSize: 12.5, color: '#64748B', whiteSpace: 'nowrap' },
  clearBtn: { background: 'none', border: '1px solid #D1D5DB', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: '#374151' },
  tableWrap: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 900 },
  th: { textAlign: 'left', fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748B', padding: '12px 16px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB', position: 'sticky', top: 0 },
  tr: { borderBottom: '1px solid #F1F5F9' },
  td: { padding: '12px 16px', fontSize: 13.5, color: '#374151', verticalAlign: 'top' },
  msgCell: { padding: '40px 16px', textAlign: 'center', color: '#64748B', fontSize: 14 },
  pill: { display: 'inline-block', background: '#EEF2FF', color: '#3730A3', borderRadius: 100, padding: '2px 10px', fontSize: 12, fontWeight: 500, textTransform: 'capitalize' },
  badge: { display: 'inline-block', borderRadius: 100, padding: '2px 10px', fontSize: 12, fontWeight: 500 },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 12 },
  pageBtn: { background: '#fff', border: '1px solid #D1D5DB', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: '#374151' },
};
